import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Users, Activity, FileText, DollarSign, UserPlus, Copy, Eye, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import CartesCodesEssai from "@/components/CartesCodesEssai";
import { dateCourte } from "@/lib/dates";

// « aucun » = compte créé mais pas encore activé (il n'y a plus d'essai gratuit).
// « illimite » = compte maison, jamais vendu.
const PLANS = ["aucun", "solo", "trio", "team", "legend", "illimite"];

/** « 0 min », « 47 min », « 3 h 20 » — lisible d'un coup d'œil dans un tableau. */
function dureeActive(minutes) {
  const m = Number(minutes) || 0;
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const reste = m % 60;
  return reste ? `${h} h ${String(reste).padStart(2, "0")}` : `${h} h`;
}

const NOMS_PLAN = {
  aucun: { fr: "Sans forfait", en: "No plan" },
  solo: { fr: "Solo — 119 $ (88 $/an) · 40", en: "Solo — $119 ($88/yr) · 40" },
  trio: { fr: "Trio — 199 $ (129 $/an) · 100 — retiré de la vente", en: "Trio — $199 ($129/yr) · 100 — no longer sold" },
  team: { fr: "Team — 199 $ (129 $/an) · 200", en: "Team — $199 ($129/yr) · 200" },
  legend: { fr: "Legend — 399 $ (279 $/an) · 500", en: "Legend — $399 ($279/yr) · 500" },
  illimite: { fr: "Illimité (maison)", en: "Unlimited (internal)" },
};

function Tuile({ icone: Icone, valeur, libelle }) {
  return (
    <div className="bg-surface border border-line rounded-xl shadow-carte p-5">
      <div className="flex items-center gap-2 text-ink-faint text-xs uppercase tracking-widest mb-2">
        <Icone className="h-3.5 w-3.5" />
        {libelle}
      </div>
      <div className="font-heading text-3xl font-extrabold tracking-tighter">{valeur}</div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [stats, setStats] = useState(null);
  const [comptes, setComptes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState("");
  // Formulaire d'ajout manuel d'un couvreur.
  const [nouveau, setNouveau] = useState({ email: "", name: "", company: "", password: "", plan: "solo" });

  // Synchronisation des prix Stripe (voir le bloc plus bas).
  const [stripeEnCours, setStripeEnCours] = useState(false);
  const [resultatStripe, setResultatStripe] = useState("");

  const synchroniserStripe = async () => {
    setStripeEnCours(true);
    setResultatStripe("");
    try {
      const { data } = await api.post("/admin/stripe/bootstrap");
      const n = (data?.plans_crees || []).length;
      setResultatStripe(n === 0
        ? (fr ? "Déjà à jour — rien à changer." : "Already up to date.")
        : (fr ? `${n} tarif(s) mis à jour chez Stripe.` : `${n} price(s) updated at Stripe.`));
      toast.success(fr ? "Prix envoyés chez Stripe" : "Prices pushed to Stripe");
    } catch (err) {
      const d = err.response?.data?.detail;
      setResultatStripe(typeof d === "string" ? d : (fr ? "Échec" : "Failed"));
      toast.error(fr ? "Envoi impossible" : "Push failed");
    } finally {
      setStripeEnCours(false);
    }
  };
  const [creation, setCreation] = useState(false);
  const [cree, setCree] = useState(null); // { email, password } — affiché une seule fois

  const fr = lang !== "en";

  const charger = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([api.get("/admin/stats"), api.get("/admin/accounts")]);
      setStats(s.data);
      setComptes(c.data.comptes || []);
    } catch {
      toast.error(fr ? "Chargement impossible" : "Could not load");
    } finally {
      setChargement(false);
    }
  }, [fr]);

  useEffect(() => {
    charger();
  }, [charger]);

  // Le serveur refuse déjà l'accès (403) ; cette garde évite d'afficher la page pour rien.
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  // Ouvre le compte d'un couvreur en consultation. Rechargement complet plutôt
  // que navigation React : le cookie de session vient de changer, tout le cache
  // en mémoire (react-query, contexte d'authentification) porte encore les
  // données de l'administrateur.
  const voirLeCompte = async (c) => {
    const qui = c.company || c.name || c.email;
    // Le mode dépend du compte cible, pas du bouton : les comptes de
    // démonstration maison s'ouvrent en accès complet, les comptes clients en
    // consultation. La confirmation doit dire lequel, sans ambiguïté.
    const message = c.demo_interne
      ? (fr
        ? `Se connecter comme ${qui} — ACCÈS COMPLET ?\n\nVos actions seront réelles : soumissions créées, courriels envoyés, quota consommé, exactement comme si vous étiez ce compte.`
        : `Sign in as ${qui} — FULL ACCESS?\n\nYour actions will be real: quotes created, emails sent, quota consumed, exactly as if you were this account.`)
      : (fr
        ? `Ouvrir le compte de ${qui} en consultation ?\n\nVous verrez exactement ce qu'il voit. Aucune modification ne sera possible, et l'accès est inscrit au journal.`
        : `Open ${qui}'s account in read-only mode?\n\nYou will see exactly what they see. No changes are possible, and the access is logged.`);
    if (!window.confirm(message)) return;
    setEnCours(c.user_id);
    try {
      await api.post(`/admin/soutien/${c.user_id}`);
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e.response?.data?.detail || (fr ? "Échec" : "Failed"));
      setEnCours("");
    }
  };

  const modifier = async (userId, corps, messageOk) => {
    setEnCours(userId);
    try {
      await api.put(`/admin/accounts/${userId}`, corps);
      toast.success(messageOk);
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || (fr ? "Échec" : "Failed"));
    } finally {
      setEnCours("");
    }
  };

  const creerCompte = async () => {
    if (!nouveau.email.trim()) { toast.error(fr ? "Courriel requis" : "Email required"); return; }
    setCreation(true);
    try {
      const { data } = await api.post("/admin/accounts", nouveau);
      setCree({ email: data.email, password: data.password });
      setNouveau({ email: "", name: "", company: "", password: "", plan: "solo" });
      toast.success(fr ? "Compte créé" : "Account created");
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || (fr ? "Échec de la création" : "Creation failed"));
    } finally {
      setCreation(false);
    }
  };

  if (chargement) {
    return (
      <Layout>
        <div className="flex justify-center py-24">
          <Loader2 className="h-7 w-7 animate-spin text-brand-ink" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Même correctif que Prospects : sans ce rembourrage, le tableau
          touchait le bord de l'écran sur téléphone. */}
      <div className="p-4 sm:p-6 md:p-10">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter">
          {fr ? "Super-administration" : "Administration"}
        </h1>
        <p className="text-ink-faint text-sm mt-1">
          {fr
            ? "Tous les comptes couvreurs, leur forfait et leur consommation."
            : "All contractor accounts, their plan and usage."}
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Tuile icone={Users} valeur={stats.comptes} libelle={fr ? "Comptes" : "Accounts"} />
          <Tuile icone={Activity} valeur={stats.actifs_ce_mois} libelle={fr ? "Actifs ce mois" : "Active this month"} />
          <Tuile icone={FileText} valeur={stats.soumissions_total} libelle={fr ? "Soumissions" : "Estimates"} />
          <Tuile
            icone={DollarSign}
            valeur={`${stats.revenu_mensuel_theorique} $`}
            libelle={fr ? "Revenu mensuel" : "Monthly revenue"}
          />
        </div>
      )}

      {/* Prix Stripe : un tarif change dans le code ne part PAS tout seul chez
          Stripe, dont les prix sont immuables. Ce bouton compare les montants
          et crée un prix neuf pour chaque écart. Les abonnés en cours gardent
          le leur — c'est voulu, on ne change pas un tarif déjà signé. */}
      <div className="bg-surface border border-line rounded-xl shadow-carte p-5 mb-8">
        <div className="flex items-center gap-2 mb-3">
          <RefreshCw className="h-4 w-4 text-brand-ink" />
          <h2 className="font-heading font-bold">
            {fr ? "Prix Stripe" : "Stripe prices"}
          </h2>
        </div>
        <p className="text-sm text-ink-soft mb-4">
          {fr
            ? "À lancer après chaque changement de tarif : les prix affichés sur le site deviennent ceux que Stripe facture."
            : "Run this after any price change: what the site shows becomes what Stripe charges."}
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={synchroniserStripe} disabled={stripeEnCours}
            data-testid="bouton-sync-stripe"
            className="bg-orange-600 hover:bg-orange-700 rounded-md font-semibold">
            {stripeEnCours && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {fr ? "Envoyer les prix chez Stripe" : "Push prices to Stripe"}
          </Button>
          {resultatStripe && (
            <span data-testid="resultat-sync-stripe" className="text-sm text-ink-soft">
              {resultatStripe}
            </span>
          )}
        </div>
      </div>

      {/* Codes d'essai : c'est par là qu'entrent les couvreurs des campagnes. */}
      <CartesCodesEssai className="bg-surface border border-line rounded-xl shadow-carte p-5 mb-8" />

      {/* Ajout manuel d'un couvreur */}
      <div className="bg-surface border border-line rounded-xl shadow-carte p-5 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="h-4 w-4 text-brand-ink" />
          <h2 className="font-heading font-bold">{fr ? "Ajouter un couvreur" : "Add a contractor"}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Input data-testid="new-account-email" type="email" placeholder={fr ? "Courriel *" : "Email *"}
            value={nouveau.email} onChange={(e) => setNouveau({ ...nouveau, email: e.target.value })}
            className="bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500" />
          <Input data-testid="new-account-name" placeholder={fr ? "Nom du contact" : "Contact name"}
            value={nouveau.name} onChange={(e) => setNouveau({ ...nouveau, name: e.target.value })}
            className="bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500" />
          <Input data-testid="new-account-company" placeholder={fr ? "Entreprise" : "Company"}
            value={nouveau.company} onChange={(e) => setNouveau({ ...nouveau, company: e.target.value })}
            className="bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500" />
          <Input data-testid="new-account-password" placeholder={fr ? "Mot de passe (vide = généré)" : "Password (empty = generated)"}
            value={nouveau.password} onChange={(e) => setNouveau({ ...nouveau, password: e.target.value })}
            className="bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500" />
          <select data-testid="new-account-plan" value={nouveau.plan}
            onChange={(e) => setNouveau({ ...nouveau, plan: e.target.value })}
            className="bg-field border border-line-strong rounded-md px-2 py-1.5 text-sm focus:border-orange-500 outline-none">
            {PLANS.map((p) => (
              <option key={p} value={p}>{NOMS_PLAN[p][fr ? "fr" : "en"]}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-4 mt-4 flex-wrap">
          <Button onClick={creerCompte} disabled={creation} data-testid="create-account-button"
            className="bg-orange-600 hover:bg-orange-700 rounded-md font-semibold">
            {creation ? <Loader2 className="h-4 w-4 animate-spin" /> : (fr ? "Créer le compte" : "Create account")}
          </Button>
          {cree && (
            <div className="flex items-center gap-3 text-sm bg-emerald-500/10 border border-emerald-500/30 rounded-md px-4 py-2">
              <span className="text-emerald-400">{cree.email}</span>
              <span className="text-ink-soft">{fr ? "mot de passe :" : "password:"}</span>
              <code className="text-ink">{cree.password}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(`${cree.email} / ${cree.password}`); toast.success(fr ? "Copié" : "Copied"); }}
                className="p-1 text-ink-soft hover:text-ink" title={fr ? "Copier" : "Copy"}>
                <Copy className="h-3.5 w-3.5" />
              </button>
              <span className="text-xs text-ink-faint">{fr ? "affiché une seule fois" : "shown only once"}</span>
            </div>
          )}
        </div>
      </div>

      <div className="bg-surface border border-line rounded-xl shadow-carte overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="border-b border-line text-ink-faint text-xs uppercase tracking-widest">
              <th className="text-left px-5 py-3 font-medium">{fr ? "Couvreur" : "Contractor"}</th>
              <th className="text-left px-5 py-3 font-medium">{fr ? "Forfait" : "Plan"}</th>
              <th className="text-left px-5 py-3 font-medium">{fr ? "Usage" : "Usage"}</th>
              <th className="text-left px-5 py-3 font-medium">{fr ? "Soumissions" : "Estimates"}</th>
              <th className="text-left px-5 py-3 font-medium">{fr ? "Prospects" : "Leads"}</th>
              <th className="text-left px-5 py-3 font-medium">{fr ? "Engagement" : "Engagement"}</th>
              <th className="text-right px-5 py-3 font-medium">{fr ? "Actions" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {comptes.map((c) => {
              const suspendu = c.statut !== "active";
              const occupe = enCours === c.user_id;
              return (
                <tr key={c.user_id} className="border-b border-line last:border-0">
                  <td className="px-5 py-4">
                    <div className="font-medium flex items-center gap-2">
                      {c.company || c.name || "—"}
                      {c.role === "admin" && (
                        <span className="text-[10px] uppercase tracking-widest bg-orange-600/20 text-brand-ink px-2 py-0.5 rounded">
                          admin
                        </span>
                      )}
                      {suspendu && (
                        <span className="text-[10px] uppercase tracking-widest bg-red-600/20 text-red-400 px-2 py-0.5 rounded">
                          {fr ? "suspendu" : "suspended"}
                        </span>
                      )}
                    </div>
                    <div className="text-ink-faint text-xs">{c.email}</div>
                  </td>
                  <td className="px-5 py-4">
                    <select
                      value={c.plan}
                      disabled={occupe}
                      onChange={(e) =>
                        modifier(c.user_id, { plan: e.target.value }, fr ? "Forfait modifié" : "Plan updated")
                      }
                      className="bg-field border border-line-strong rounded px-2 py-1.5 text-sm focus:border-orange-500 outline-none"
                    >
                      {PLANS.map((p) => (
                        <option key={p} value={p}>
                          {NOMS_PLAN[p][fr ? "fr" : "en"]}
                        </option>
                      ))}
                    </select>
                    {c.sans_forfait && (
                      <div className="text-xs mt-1 text-amber-700">
                        {fr ? "à activer" : "not activated"}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4 whitespace-nowrap">
                    {c.sans_forfait ? (
                      <span className="text-amber-700">{fr ? "bloqué" : "blocked"}</span>
                    ) : c.limite_mensuelle === 0 ? (
                      <span className="text-ink-soft">{fr ? "illimité" : "unlimited"}</span>
                    ) : (
                      <>
                        <span className={c.restant === 0 ? "text-red-400" : ""}>
                          {Math.min(c.utilise, c.limite_mensuelle)} / {c.limite_mensuelle}
                        </span>
                        {c.depassement > 0 && (
                          <div className="text-xs mt-1 text-amber-700">
                            +{c.depassement} {fr ? "en surplus" : "over"} = {c.facture_surplus.toLocaleString(fr ? "fr-CA" : "en-CA")} $
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-5 py-4 text-ink-soft">{c.nb_soumissions}</td>
                  <td className="px-5 py-4 text-ink-soft">{c.nb_prospects}</td>
                  {/* Engagement : connexions, temps réellement passé dans
                      l'outil, et date de la dernière visite. C'est le signal
                      d'alerte le plus fiable avant une annulation. */}
                  <td className="px-5 py-4 whitespace-nowrap">
                    <div className="text-ink-soft">
                      {c.nb_connexions} {fr ? "connexions" : "logins"}
                    </div>
                    <div className="text-xs text-ink-faint">
                      {dureeActive(c.minutes_actives)}
                      {c.derniere_connexion && (
                        <> · {fr ? "vu" : "seen"} {dateCourte(c.derniere_connexion, lang)}</>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex gap-2 justify-end flex-wrap">
                      {/* Soutien technique : ouvre le compte du couvreur en
                          CONSULTATION. Le serveur refuse toute écriture pendant
                          la session — l'interface ne fait que l'annoncer. */}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={occupe || c.user_id === user?.user_id}
                        onClick={() => voirLeCompte(c)}
                        data-testid={`soutien-${c.user_id}`}
                        className={`h-8 text-xs bg-transparent hover:bg-hover ${
                          c.demo_interne ? "border-red-500/60 text-red-400" : "border-line-strong"}`}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1.5" />
                        {c.demo_interne
                          ? (fr ? "Se connecter" : "Sign in as")
                          : (fr ? "Voir son compte" : "View account")}
                      </Button>
                      {/* Bascule du drapeau « compte de démonstration ». À ne
                          poser que sur des comptes maison : il donne l'accès
                          complet en écriture depuis l'administration. */}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={occupe}
                        onClick={() => {
                          if (!c.demo_interne && !window.confirm(fr
                            ? `Marquer ${c.company || c.email} comme compte de démonstration ?\n\nVous pourrez alors vous y connecter en ACCÈS COMPLET depuis l'administration. À ne faire que sur vos propres comptes, jamais sur celui d'un client.`
                            : `Mark ${c.company || c.email} as a demo account?\n\nYou will then be able to sign in with FULL ACCESS from the admin panel. Only do this on your own accounts, never a client's.`)) return;
                          modifier(c.user_id, { demo_interne: !c.demo_interne },
                            c.demo_interne
                              ? (fr ? "N'est plus un compte de démonstration" : "No longer a demo account")
                              : (fr ? "Marqué comme compte de démonstration" : "Marked as demo account"));
                        }}
                        data-testid={`demo-${c.user_id}`}
                        className={`h-8 text-xs bg-transparent hover:bg-hover ${
                          c.demo_interne ? "border-red-500/60 text-red-400" : "border-line-strong text-ink-faint"}`}
                        title={fr ? "Compte de démonstration maison" : "Internal demo account"}
                      >
                        {c.demo_interne ? (fr ? "Démo ✓" : "Demo ✓") : (fr ? "Démo" : "Demo")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={occupe}
                        onClick={() =>
                          modifier(c.user_id, { remettre_quota: true }, fr ? "Quota remis à zéro" : "Quota reset")
                        }
                        className="h-8 text-xs border-line-strong bg-transparent hover:bg-hover"
                      >
                        {fr ? "Remettre le quota" : "Reset quota"}
                      </Button>
                      <Button
                        size="sm"
                        disabled={occupe || c.user_id === user?.user_id}
                        onClick={() =>
                          modifier(
                            c.user_id,
                            { plan_status: suspendu ? "active" : "suspendu" },
                            suspendu ? (fr ? "Compte réactivé" : "Reactivated") : (fr ? "Compte suspendu" : "Suspended")
                          )
                        }
                        className={`h-8 text-xs ${
                          suspendu ? "bg-green-700 hover:bg-green-600" : "bg-red-800 hover:bg-red-700"
                        }`}
                      >
                        {suspendu ? (fr ? "Réactiver" : "Reactivate") : (fr ? "Suspendre" : "Suspend")}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!comptes.length && (
              <tr>
                <td colSpan={7} className="px-5 py-12 text-center text-ink-faint">
                  {fr ? "Aucun compte." : "No accounts."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>
    </Layout>
  );
}
